<template>
  <div
    v-if="data.notesLoaded"
    class="i-meeting-notes"
  >
    <div
      v-if="data.meetingNotes.length"
      class="list"
    >
      <iMeetingNoteCard
        v-for="(meetingNote, index) in meetngNotesToShow"
        :key="meetingNote.id"
        :meeting-notes="meetingNote"
        :same-stage-as-last="index > 0 ? meetngNotesToShow[index - 1].careerStage == meetingNote.careerStage : false"
        :same-stage-as-next="
          index < meetngNotesToShow.length - 1
            ? meetngNotesToShow[index + 1].careerStage == meetingNote.careerStage
            : false
        "
        :is-latest="index === 0"
        @refresh-meeting-notes="fetchMeetingNotes"
      />
    </div>
    <div
      v-else
      class="empty"
    >
      You have no meeting notes yet!
    </div>
    <iBaseButton
      v-if="data.meetingNotes.length > 4"
      v-bind="MAIN_BUTTON_STYLE"
      text="Show all meeting notes"
      :icon-url="iconArrow"
      icon-width="22px"
      @clicked="goToMeetingNotes"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive } from "vue"
import { useRouter } from "vue-router"
import { useUserStore } from "@/stores/user"
import { bluePurpleGradient, white } from "@/styles/colors"

import { GlobalVariables } from "@/helpers/globalVariables"
import api from "@/services/apiClient"

import iBaseButton from "@/components/base/BaseButton.vue"
import iMeetingNoteCard from "@/components/coaching/home/MeetingNoteCard.vue"

// ASSETS
import iconArrow from "@/assets/icons/icon-arrow-right-white.svg"

// TYPES
import { MeetingNotesUserAccess } from "@/shared/types/MeetingNotes"

// STORES AND ROUTERS
const userStore = useUserStore()
const router = useRouter()

// CONSTANTS
const MAIN_BUTTON_STYLE = {
  backgroundImage: bluePurpleGradient,
  borderRadius: "8px",
  fontWeight: "500",
  textColor: white,
  width: "100%",
  padding: "10px 16px",
  size: "small"
}

// DATA
const data = reactive({
  notesLoaded: false,
  meetingNotes: [] as MeetingNotesUserAccess[]
})

// COMPUTED
const meetngNotesToShow = computed(() =>
  data.meetingNotes.length > 4 ? data.meetingNotes.slice(0, -1) : data.meetingNotes
)

// FUNCTIONS
const fetchMeetingNotes = async () => {
  if (userStore.getUserId) {
    try {
      const response = await api.meeting.getListForClient(userStore.getUserId, {
        limit: 5,
        page: 1,
        orderBy: "date",
        direction: "desc",
        status: ["assistedDelivered", "delivered"],
        type: [
          "discovery",
          "coaching",
          "executiveCoaching",
          "recurring",
          "onDemand",
          "scoping",
          "scopingRescheduled",
          "proPackage"
        ]
      })
      data.meetingNotes = response.meetingNotes
    } catch {
      console.error(`Error fetching user's meeting notes. User ID: ${userStore.getUserId}`)
    }
  }
}

const goToMeetingNotes = () => {
  router.push({
    name: GlobalVariables.urls.coaching.children.meetingNotes.routeName
  })
}

// LIFECYCLE HOOKS
onMounted(async () => {
  await fetchMeetingNotes()
  data.notesLoaded = true
})
</script>

<style lang="scss" scoped>
.i-meeting-notes {
  .list {
    @include flex(column, start, start);
  }

  .empty {
    @include text-lg;

    color: $black400;
  }
}
</style>
